<template>
  <div class="elvs" ref="root">
    <!-- Trigger input -->
    <div class="elvs__fieldWrap">
      <input
        ref="input"
        class="elvs__field"
        :placeholder="placeholder"
        :value="selectedLabel"
        readonly
        @click="open"
        @focus="open"
      />
      <button
        v-if="value"
        class="elvs__iconBtn"
        type="button"
        title="Clear"
        @mousedown.prevent
        @click="clear"
      >
        ✕
      </button>
      <button
        class="elvs__iconBtn"
        type="button"
        title="Open"
        @mousedown.prevent
        @click="open"
      >
        ▴
      </button>
    </div>

    <!-- Backdrop (click outside closes) -->
    <div v-if="openState" class="elvs__backdrop" @mousedown.prevent="close"></div>

    <!-- Popup -->
    <div
      v-if="openState"
      class="elvs__popup"
      :style="popupStyle"
      role="dialog"
      aria-modal="true"
      @mousedown.stop
    >
      <!-- Header: search -->
      <div class="elvs__header">
        <div class="elvs__searchRow">
          <span class="elvs__searchIcon">⌕</span>
          <input
            ref="search"
            v-model="search"
            class="elvs__search"
            type="text"
            placeholder="Search shared voices…"
            @keydown.esc.prevent="close"
            @keydown.down.prevent="focusFirstRow"
          />
          <button
            v-if="search"
            class="elvs__smallBtn"
            type="button"
            @mousedown.prevent
            @click="search=''; page=0; fetchPage()"
          >
            Clear
          </button>
        </div>

        <div class="elvs__subRow">
          <div class="elvs__status">
            <span v-if="loading">Loading…</span>
            <span v-else>
              Page {{ page }}
              <span v-if="totalCount !== null">• {{ totalCount }} total</span>
              <span v-else-if="hasMore">• more available</span>
            </span>
          </div>
          <button class="elvs__smallBtn" type="button" @mousedown.prevent @click="close">Close</button>
        </div>

        <div v-if="error" class="elvs__error">{{ error }}</div>
      </div>

      <!-- Body: list -->
      <div class="elvs__body" @scroll.passive="onScroll">
        <div v-if="!loading && items.length === 0" class="elvs__empty">
          No voices found.
        </div>

        <div
          v-for="(item, idx) in items"
          :key="item._rawId"
          class="elvs__row"
          tabindex="0"
          @keydown.enter.prevent="pick(item)"
          @keydown.up.prevent="focusPrev(idx)"
          @keydown.down.prevent="focusNext(idx)"
          @click="pick(item)"
        >
          <div class="elvs__rowMain">
            <div class="elvs__title">
              <span class="elvs__name">{{ item._displayName }}</span>
              <span v-if="item._category" class="elvs__badge">{{ item._category }}</span>
              <span v-if="item._isAdded" class="elvs__badge elvs__badge--ok">Added</span>
            </div>

            <div class="elvs__meta">{{ metaLine(item) }}</div>

            <div v-if="item._description" class="elvs__desc">
              {{ item._description }}
            </div>

            <div v-if="item._descriptives && item._descriptives.length" class="elvs__chips">
              <span
                v-for="(d, i) in item._descriptives.slice(0, 8)"
                :key="item._rawId + '-d-' + i"
                class="elvs__chip"
              >{{ d }}</span>
              <span v-if="item._descriptives.length > 8" class="elvs__more">
                +{{ item._descriptives.length - 8 }} more
              </span>
            </div>
          </div>

          <div class="elvs__rowActions" @click.stop>
            <button
              v-if="item._previewUrl"
              class="elvs__actionBtn"
              type="button"
              @mousedown.prevent
              @click="togglePreview(item)"
              :title="isPlaying(item) ? 'Pause' : 'Play preview'"
            >
              {{ isPlaying(item) ? "❚❚" : "▶" }}
            </button>

            <!-- <button
              v-if="!item._isAdded"
              class="elvs__actionBtn elvs__actionBtn--primary"
              type="button"
              @mousedown.prevent
              @click="addVoice(item, { alsoSelect: true })"
              :disabled="addingId && addingId !== item._rawId"
            >
              <span v-if="addingId === item._rawId">Adding…</span>
              <span v-else>Add</span>
            </button> -->
          </div>
        </div>
      </div>

      <!-- Footer: pagination -->
      <div class="elvs__footer">
        <button
          class="elvs__footerBtn"
          type="button"
          @mousedown.prevent
          @click="prevPage"
          :disabled="page <= 1 || loadingMore"
        >
          Prev
        </button>

        <button
          class="elvs__footerBtn"
          type="button"
          @mousedown.prevent
          @click="nextPage"
          :disabled="!hasMore || loadingMore"
        >
          <span v-if="loadingMore">Loading…</span>
          <span v-else>Next</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

/**
 * Vue 2 v-model:
 * <ElevenLabsVoiceSelect v-model="voiceId" />
 * Emits: input (added voice_id)
 */
export default {
  name: "ElevenLabsVoiceSelect",

  props: {
    value: { type: String, default: null }, // added voice_id
    valueName: { type: String, default: null },
    valuePreviewUrl: { type: String, default: null },
    placeholder: { type: String, default: "Select a voice…" },
    pageSize: { type: Number, default: 25 },
    autoFetchOnOpen: { type: Boolean, default: true },
    defaultNewNameSuffix: { type: String, default: " (added)" },
  },
  data() {
    return {
      openState: false,
      popupStyle: {},

      search: "",
      _searchTimer: null,

      items: [],
      page: 0,
      hasMore: false,
      totalCount: null,

      loading: false,
      loadingMore: false,
      error: null,

      addingId: null,

      selectedName: null,
      selectedVoiceId: this.value,

      // audio preview
      audio: null,
      playingRawId: null,

      // request guard
      _reqId: 0,
    };
  },
  computed: {
    selectedLabel() {
      if (!this.selectedVoiceId) return "";
      return this.selectedName || this.valueName || this.selectedVoiceId;
    },
  },
  watch: {
    valueName(n) {
      if (n) this.selectedName = n;
    },
    value(v) {
      this.selectedVoiceId = v;
      const match = this.items.find(
        (x) => x._addedVoiceId === v || x._value === v
      );
      if (match) this.selectedName = match._displayName;
    },
    search() {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        this.page = 0;
        this.fetchPage();
      }, 300);
    },
  },
  mounted() {
    window.addEventListener("resize", this.reposition, { passive: true });
    window.addEventListener("scroll", this.reposition, { passive: true, capture: true });
    document.addEventListener("keydown", this.onGlobalKeydown);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.reposition);
    window.removeEventListener("scroll", this.reposition, true);
    document.removeEventListener("keydown", this.onGlobalKeydown);
    this.stopAudio();
  },
  methods: {
    open() {
      this.openState = true;
      this.reposition();

      this.$nextTick(() => {
        // focus search for fast typing
        if (this.$refs.search) this.$refs.search.focus();

        if (this.autoFetchOnOpen && this.items.length === 0) {
          this.fetchPage();
        }
      });
    },
    close() {
      this.openState = false;
      this.stopAudio();
    },
    clear() {
      this.selectedVoiceId = null;
      this.selectedName = null;
      this.$emit("input", null);
    },
    onGlobalKeydown(e) {
      if (!this.openState) return;
      if (e.key === "Escape") {
        e.preventDefault();
        this.close();
      }
    },
    reposition() {
      if (!this.openState) return;
      const el = this.$refs.input;
      if (!el) return;

      const r = el.getBoundingClientRect();
      const width = Math.max(420, r.width);

      // FIXED popup that always opens UP.
      const popupHeight = 520; // you can tweak
      const gap = 8;

      const top = Math.max(8, r.top - gap - popupHeight);
      const left = Math.min(window.innerWidth - width - 8, Math.max(8, r.left));

      this.popupStyle = {
        position: "fixed",
        top: `${top}px`,
        left: `${left}px`,
        width: `${width}px`,
        height: `${popupHeight}px`,
        zIndex: 9999,
      };
    },

    async fetchPage({ isPaging = false } = {}) {
      const reqId = ++this._reqId;
      this.error = null;
      if (!isPaging) this.loading = true;
      else this.loadingMore = true;

      try {
        const { data } = await axios.get(`${process.env.VUE_APP_API_ENDPOINT}` + "/elevenlabs/voices", {
          params: {
            page: this.page,
            page_size: this.pageSize,
            search: this.search || undefined,
          },
        }, {
          headers: {
            'Authorization': 'Bearer ' + this.$store.state.auth.jwt
          }
        });

        // if (reqId !== this._reqId) return;

        const list = data.voices || data.shared_voices || data.items || data.data || [];
        // console.log('list', list)
        this.items = (list || []).map(this.normalizeSharedVoice);
        // console.log('items', this.items)

        this.totalCount =
          typeof data.total_count === "number"
            ? data.total_count
            : typeof data.total === "number"
            ? data.total
            : null;

        this.hasMore =
          Boolean(data.has_more) ||
          (typeof data.total_pages === "number" ? this.page < data.total_pages : false) ||
          (Array.isArray(list) && list.length === this.pageSize);

        // try to resolve selectedName
        if (this.selectedVoiceId) {
          const match = this.items.find(
            (x) => x._addedVoiceId === this.selectedVoiceId || x._value === this.selectedVoiceId
          );
          if (match) this.selectedName = match._displayName;
        }

        // keep popup anchored
        this.$nextTick(this.reposition);
      } catch (e) {
        console.log('e', e)
        this.error = this.humanError(e);
      } finally {
        this.loading = false;
        this.loadingMore = false;
      }
    },

    async nextPage() {
      if (!this.hasMore) return;
      this.page += 1;
      await this.fetchPage({ isPaging: true });
    },
    async prevPage() {
      if (this.page <= 1) return;
      this.page -= 1;
      await this.fetchPage({ isPaging: true });
    },

    async pick(item) {
      if (!item) return;

      if (item._isAdded && item._addedVoiceId) {
        this.selectedVoiceId = item._addedVoiceId;
        this.selectedName = item._displayName;
        this.$emit("input", item._addedVoiceId);              // keep v-model for compatibility
        this.$emit("change", { id: item._addedVoiceId, name: item._displayName, previewUrl: item._previewUrl }); // extra info
        this.close();
        return;
      }

      // If not yet added, add+select
      await this.addVoice(item, { alsoSelect: true });
      this.close();
    },

    async addVoice(item, { alsoSelect = false } = {}) {
      const publicUserId = item._publicUserId;
      const sharedVoiceId = item._sharedVoiceId;

      if (!publicUserId || !sharedVoiceId) {
        this.error = "Missing public_user_id / voice_id on this item.";
        return;
      }

      this.addingId = item._rawId;
      this.error = null;

      try {
        const newName = `${item._displayName}${this.defaultNewNameSuffix}`;

        const { data } = await axios.post(`${process.env.VUE_APP_API_ENDPOINT}` + "/elevenlabs/add-voice", {
          public_user_id: publicUserId,
          voice_id: sharedVoiceId,
          new_name: newName,
        }, {
          headers: {
            'Authorization': 'Bearer ' + this.$store.state.auth.jwt
          }
        });

        const addedVoiceId =
          data.voice_id ||
          data.voice.voice_id ||
          data.added_voice_id ||
          null;

        item._isAdded = true;
        item._addedVoiceId = addedVoiceId || sharedVoiceId;
        item._value = item._addedVoiceId;

        if (alsoSelect) {
          this.selectedVoiceId = item._addedVoiceId;
          this.selectedName = item._displayName;
          this.$emit("input", item._addedVoiceId);              // keep v-model for compatibility
          this.$emit("change", { id: item._addedVoiceId, name: item._displayName, previewUrl: item._previewUrl }); // extra info
        }
      } catch (e) {
        this.error = this.humanError(e);
      } finally {
        this.addingId = null;
      }
    },

    metaLine(v) {
      const parts = [];
      if (v._accent) parts.push(v._accent);
      if (v._country) parts.push(v._country);
      if (v._gender) parts.push(v._gender);
      if (v._age) parts.push(v._age);
      if (v._language && !parts.includes(v._language)) parts.unshift(v._language);
      return parts.length ? parts.join(" • ") : "—";
    },

    normalizeSharedVoice(raw) {
      // shared voice id might be raw.voice_id or raw.shared_voice_id depending on API response
      const sharedVoiceId = raw.voice_id || raw.shared_voice_id || raw.id || null;

      // public user/owner might have many names
      const publicUserId =
        raw.public_user_id ||
        raw.public_owner_id ||
        raw.publicOwnerId ||
        raw.publicUserId ||
        raw.user_id ||
        raw.owner_id ||
        (raw.public_owner && (raw.public_owner.id || raw.public_owner.public_user_id)) ||
        null;

      const descriptives = raw.descriptive.split(",");

      const category = raw.category;

      const previewUrl = raw.preview_url;

      // If already added, response might contain something like "is_added" or "added_voice_id"
      const addedVoiceId = raw.added_voice_id || raw.addedVoiceId || raw._addedVoiceId || null;
      const isAdded = Boolean(raw.is_added || raw.isAdded || addedVoiceId);

      return {
        ...raw,

        // internal normalized fields for the UI
        _rawId: `${publicUserId || "unknown"}:${sharedVoiceId || "unknown"}`,
        _publicUserId: publicUserId,
        _sharedVoiceId: sharedVoiceId,

        _displayName: raw.name,
        _description: raw.description,
        _descriptives: Array.isArray(descriptives) ? descriptives : [],
        _category: category,

        _accent: raw.accent,
        _gender: raw.gender,
        _age: raw.age,
        _language: raw.language,

        _previewUrl: previewUrl,

        _isAdded: isAdded,
        _addedVoiceId: addedVoiceId,

        // what v-autocomplete uses as value:
        // if already added, prefer the added voice id; otherwise use shared voice id
        _value: (addedVoiceId || sharedVoiceId),
      };
    },

    togglePreview(item) {
      if (!item || !item._previewUrl) return;

      if (this.isPlaying(item)) {
        this.stopAudio();
        return;
      }

      this.stopAudio();
      try {
        this.audio = new Audio(item._previewUrl);
        this.playingRawId = item._rawId;

        this.audio.addEventListener("ended", () => {
          this.playingRawId = null;
          this.audio = null;
        });

        this.audio.play().catch(() => {
          this.playingRawId = null;
          this.audio = null;
        });
      } catch (e) {}
    },
    isPlaying(item) {
      return item && this.playingRawId === item._rawId;
    },
    stopAudio() {
      try { if (this.audio) this.audio.pause(); } catch (e) {}
      this.audio = null;
      this.playingRawId = null;
    },

    focusFirstRow() {
      this.$nextTick(() => {
        const first = this.$el.querySelector(".elvs__row");
        if (first) first.focus();
      });
    },
    focusNext(idx) {
      this.$nextTick(() => {
        const rows = this.$el.querySelectorAll(".elvs__row");
        if (rows && rows[idx + 1]) rows[idx + 1].focus();
      });
    },
    focusPrev(idx) {
      this.$nextTick(() => {
        const rows = this.$el.querySelectorAll(".elvs__row");
        if (rows && rows[idx - 1]) rows[idx - 1].focus();
        else if (this.$refs.search) this.$refs.search.focus();
      });
    },

    onScroll() {
      // optional: you can implement infinite scroll here if you later switch to token-based pagination
    },

    humanError(e) {
      const msg =
        e.response.data.message ||
        e.response.data.error.detail ||
        e.response.data.error ||
        e.message ||
        "Request failed";
      const status = e.response.status;
      if (status) return `(${status}) ${typeof msg === "string" ? msg : JSON.stringify(msg)}`;
      return typeof msg === "string" ? msg : JSON.stringify(msg);
    },
  },
};
</script>

<style scoped>
.elvs { position: relative; font-family: system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif; }

.elvs__fieldWrap {
  position: relative;
  display: flex;
  align-items: center;
}

.elvs__field {
  width: 100%;
  padding: 10px 68px 10px 12px;
  border: 1px solid rgba(0,0,0,0.25);
  border-radius: 10px;
  font-size: 13px;
  outline: none;
  background: #fff;
}
.elvs__field:focus { border-color: rgba(0,0,0,0.45); box-shadow: 0 0 0 3px rgba(0,0,0,0.06); }

.elvs__iconBtn {
  position: absolute;
  right: 10px;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid rgba(0,0,0,0.18);
  background: #fff;
  cursor: pointer;
  font-size: 12px;
  line-height: 1;
}
.elvs__iconBtn:first-of-type { right: 42px; }
.elvs__iconBtn:hover { background: rgba(0,0,0,0.04); }

.elvs__backdrop {
  position: fixed;
  inset: 0;
  z-index: 9998;
  background: rgba(0,0,0,0.10);
}

.elvs__popup {
  z-index: 9999;
  background: #fff;
  border: 1px solid rgba(0,0,0,0.18);
  border-radius: 14px;
  box-shadow: 0 18px 60px rgba(0,0,0,0.22);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.elvs__header { padding: 12px 12px 10px; border-bottom: 1px solid rgba(0,0,0,0.08); }
.elvs__searchRow { display: flex; align-items: center; gap: 8px; }
.elvs__searchIcon { font-size: 13px; opacity: 0.65; }

.elvs__search {
  flex: 1;
  padding: 9px 10px;
  border: 1px solid rgba(0,0,0,0.20);
  border-radius: 10px;
  font-size: 13px;
  outline: none;
}
.elvs__search:focus { border-color: rgba(0,0,0,0.45); box-shadow: 0 0 0 3px rgba(0,0,0,0.06); }

.elvs__subRow { display:flex; align-items:center; justify-content: space-between; margin-top: 10px; }
.elvs__status { font-size: 12px; color: rgba(0,0,0,0.65); }
.elvs__error { margin-top: 8px; font-size: 12px; color: #b00020; }

.elvs__smallBtn {
  padding: 7px 10px;
  border-radius: 10px;
  border: 1px solid rgba(0,0,0,0.18);
  background: #fff;
  cursor: pointer;
  font-size: 12px;
}
.elvs__smallBtn:hover { background: rgba(0,0,0,0.04); }

.elvs__body {
  flex: 1;
  overflow: auto;
  padding: 6px;
}

.elvs__empty { padding: 10px; color: rgba(0,0,0,0.60); font-size: 12px; }

.elvs__row {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding: 10px;
  border-radius: 12px;
  cursor: pointer;
  outline: none;
}
.elvs__row:hover { background: rgba(0,0,0,0.04); }
.elvs__row:focus { box-shadow: inset 0 0 0 2px rgba(0,0,0,0.18); }

.elvs__title { display:flex; align-items:center; flex-wrap: wrap; gap: 8px; }
.elvs__name { font-size: 13px; font-weight: 650; }
.elvs__badge {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid rgba(0,0,0,0.18);
  color: rgba(0,0,0,0.70);
}
.elvs__badge--ok { border-color: rgba(0,140,0,0.35); color: rgba(0,120,0,0.95); }

.elvs__meta { margin-top: 3px; font-size: 12px; color: rgba(0,0,0,0.65); }
.elvs__desc { margin-top: 4px; font-size: 12px; color: rgba(0,0,0,0.55); }

.elvs__chips { margin-top: 8px; display:flex; flex-wrap: wrap; gap: 6px; }
.elvs__chip {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid rgba(0,0,0,0.18);
  background: rgba(0,0,0,0.02);
}
.elvs__more { font-size: 11px; color: rgba(0,0,0,0.55); align-self: center; }

.elvs__rowActions { display:flex; align-items:center; gap: 8px; }

.elvs__actionBtn {
  padding: 7px 10px;
  border-radius: 10px;
  border: 1px solid rgba(0,0,0,0.18);
  background: #fff;
  cursor: pointer;
  font-size: 12px;
}
.elvs__actionBtn:hover { background: rgba(0,0,0,0.04); }
.elvs__actionBtn:disabled { opacity: 0.55; cursor: not-allowed; }

.elvs__actionBtn--primary {
  border-color: rgba(0,0,0,0.28);
  font-weight: 600;
}

.elvs__footer {
  padding: 10px 12px;
  border-top: 1px solid rgba(0,0,0,0.08);
  display:flex;
  justify-content: flex-end;
  gap: 10px;
  background: rgba(0,0,0,0.015);
}
.elvs__footerBtn {
  padding: 8px 12px;
  border-radius: 10px;
  border: 1px solid rgba(0,0,0,0.18);
  background: #fff;
  cursor: pointer;
  font-size: 12px;
}
.elvs__footerBtn:hover { background: rgba(0,0,0,0.04); }
.elvs__footerBtn:disabled { opacity: 0.55; cursor: not-allowed; }
</style>